/**
 * aiProxyClient
 * -------------
 * Single client-side entry point for ALL AI calls.
 *
 * Every AI request goes through the `ai-proxy` Supabase Edge Function, which
 * holds the provider key server-side (never shipped to the browser), verifies
 * the caller's JWT, enforces per-user rate limits, and enforces the per-org
 * monthly token budget. The browser must NEVER talk to OpenAI / OpenRouter
 * directly — doing so would expose the API key to anyone with devtools.
 *
 * The proxy chooses the model per `operation`; the client cannot pick an
 * arbitrary model. Keep the operation list in sync with the edge function's
 * MODELS map (supabase/functions/ai-proxy/index.ts).
 */

import { supabase } from '@/lib/supabase'

export type AIOperation = 'chat' | 'classification' | 'onboarding'

export interface AIProxyMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface AIProxyRequest {
  operation: AIOperation
  messages: AIProxyMessage[]
  /** Ask the model to return a single JSON object. */
  jsonMode?: boolean
  /** Optional document id, recorded against token usage for attribution. */
  documentId?: string
}

export interface AIProxyResponse {
  content: string
  tokensUsed: number
}

/**
 * Human-friendly messages for the status codes the edge function returns.
 * Anything not listed falls back to the server-provided error string.
 */
function messageForStatus(status: number, serverError?: string): string {
  switch (status) {
    case 401:
      return 'Your session has expired. Please sign in again to use AI features.'
    case 402:
      return 'Your organisation has reached its monthly AI usage limit. Upgrade your plan to continue.'
    case 403:
      return 'AI requests are not allowed from this origin.'
    case 413:
      return 'That request is too large to process. Try a smaller document or shorter message.'
    case 429:
      return 'Too many AI requests right now. Please wait a moment and try again.'
    case 502:
    case 503:
      return 'The AI service is temporarily unavailable. Please try again shortly.'
    default:
      return serverError || `AI request failed (${status}). Please try again.`
  }
}

/**
 * Invoke the ai-proxy edge function. Throws a friendly Error on failure.
 */
export async function callAIProxy(request: AIProxyRequest): Promise<AIProxyResponse> {
  // Ensure there is an authenticated session; the proxy rejects anonymous calls.
  const { data: sessionData } = await supabase.auth.getSession()
  if (!sessionData.session) {
    throw new Error('You must be signed in to use AI features.')
  }

  // supabase.functions.invoke automatically attaches the Authorization header
  // with the user's access token and the apikey header.
  const { data, error } = await supabase.functions.invoke<AIProxyResponse & { error?: string }>(
    'ai-proxy',
    {
      body: {
        operation: request.operation,
        messages: request.messages,
        jsonMode: request.jsonMode ?? false,
        documentId: request.documentId ?? null,
      },
    }
  )

  // The functions client surfaces non-2xx responses as a FunctionsHttpError
  // whose `context` is the Response object. Pull the status/body for a good message.
  if (error) {
    const ctx = (error as unknown as { context?: Response }).context
    if (ctx && typeof ctx.status === 'number') {
      let serverError: string | undefined
      try {
        const bodyText = await ctx.clone().text()
        serverError = JSON.parse(bodyText)?.error
      } catch {
        // ignore parse failures; fall back to status-based message
      }
      throw new Error(messageForStatus(ctx.status, serverError))
    }
    throw new Error('Unable to reach the AI service. Please check your connection and try again.')
  }

  if (!data || typeof data.content !== 'string') {
    throw new Error('The AI service returned an unexpected response. Please try again.')
  }

  return { content: data.content, tokensUsed: data.tokensUsed ?? 0 }
}

/**
 * Whether AI features are available to the current user.
 *
 * Note: with the proxy architecture the browser no longer knows whether a
 * provider key is configured (that's a server secret). AI is considered
 * "available" whenever the user is authenticated; the proxy returns a clear
 * 503 if the server key is missing, which the UI surfaces on use.
 */
export async function isAIAvailable(): Promise<boolean> {
  const { data } = await supabase.auth.getSession()
  return !!data.session
}
